import React, { useState, useEffect, useRef } from "react";
import TextEditor from "../../Main/TextEditor";
import middleware from "../../Api/Middleware";
import ToastSuccess from "../../Toasts/ToastSuccess";
import ToastError from "../../Toasts/ToastError";

/**
 * SalesChannelDescriptionEditor Component
 * 
 * A reusable component for managing sales channel-specific descriptions.
 * Can be used for products, categories, and brands.
 * 
 * @param {Object} props
 * @param {string} props.entityType - Type of entity: 'product', 'category', or 'brand'
 * @param {number|string} props.entityId - ID of the entity (required for edit mode)
 * @param {string} props.defaultDescription - The default/standard description
 * @param {Function} props.onDefaultDescriptionChange - Callback when default description changes
 * @param {boolean} props.isEditMode - Whether in edit mode (has entityId) or create mode
 * @param {Object} props.channelDescriptions - For create mode: object with channel descriptions
 * @param {Function} props.onChannelDescriptionsChange - For create mode: callback to update channel descriptions
 * @param {boolean} props.useTextarea - Use textarea instead of TextEditor (for simple modals)
 * @param {string} props.label - Label text for the description field
 */
const SalesChannelDescriptionEditor = ({
  entityType = 'product',
  entityId = null,
  defaultDescription = '',
  onDefaultDescriptionChange = () => {},
  isEditMode = false,
  channelDescriptions: externalChannelDescriptions = {},
  onChannelDescriptionsChange = () => {},
  useTextarea = false,
  label = 'Omschrijving'
}) => {
  const [salesChannels, setSalesChannels] = useState([]);
  const [channelDescriptions, setChannelDescriptions] = useState({});
  const [selectedDescriptionChannel, setSelectedDescriptionChannel] = useState('default');
  const [currentDescription, setCurrentDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const saveTimeoutRef = useRef(null);
  const editorKey = useRef(0);

  // Helper function to get correct plural form for API endpoints
  const getEntityEndpoint = () => {
    const endpointMap = {
      'product': 'products',
      'category': 'products/categories',
      'brand': 'products/brands'
    };
    return endpointMap[entityType] || `${entityType}s`;
  };

  // Fetch sales channels on component mount
  useEffect(() => {
    fetchSalesChannels();
    if (isEditMode && entityId) {
      fetchChannelDescriptions();
    }
  }, [entityId, isEditMode]);

  // For create mode, sync external channel descriptions
  useEffect(() => {
    if (!isEditMode) {
      setChannelDescriptions(externalChannelDescriptions);
    }
  }, [externalChannelDescriptions, isEditMode]);

  // Update current description when selected channel changes
  useEffect(() => {
    let newDescription = '';
    if (selectedDescriptionChannel === 'default') {
      newDescription = defaultDescription || '';
    } else {
      const channelDesc = channelDescriptions[selectedDescriptionChannel];
      newDescription = channelDesc || defaultDescription || '';
    }
    
    if (newDescription !== currentDescription) {
      setCurrentDescription(newDescription);
      editorKey.current += 1;
      setHasChanges(false);
    }
  }, [selectedDescriptionChannel, channelDescriptions, defaultDescription]);

  const fetchSalesChannels = async () => {
    try {
      const response = await middleware.get('sp/salesChannelsTableAll?page=1&page_size=1000&filtering=status=Actief');
      setSalesChannels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sales channels:', error);
    }
  };

  const fetchChannelDescriptions = async () => {
    if (!entityId || !isEditMode) return;
    
    try {
      const endpoint = `${getEntityEndpoint()}/${entityId}/sales-channel-descriptions`;
      const response = await middleware.get(endpoint);
      setChannelDescriptions(response.data.data || {});
    } catch (error) {
      console.error('Error fetching channel descriptions:', error);
    }
  };

  const saveChannelDescription = async (channelId, description) => {
    if (!entityId || !isEditMode) {
      // In create mode, update external state
      const newDescriptions = { ...channelDescriptions };
      if (description === '' || description === null) {
        delete newDescriptions[channelId];
      } else {
        newDescriptions[channelId] = description;
      }
      setChannelDescriptions(newDescriptions);
      onChannelDescriptionsChange(newDescriptions);
      setHasChanges(false);
      return;
    }
    
    try {
      const endpoint = `${getEntityEndpoint()}/${entityId}/sales-channel-descriptions`;
      await middleware.put(endpoint, {
        sales_channel_id: channelId,
        description: description
      });
      
      if (description === '' || description === null) {
        setChannelDescriptions(prev => {
          const newDescriptions = { ...prev };
          delete newDescriptions[channelId];
          return newDescriptions;
        });
      } else {
        setChannelDescriptions(prev => ({
          ...prev,
          [channelId]: description
        }));
      }
      
      ToastSuccess('Verkoopkanaal omschrijving opgeslagen', 'channel_description_saved', 2000);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving channel description:', error);
      ToastError('Fout bij het opslaan van de omschrijving');
    }
  };

  const handleDescriptionChange = (value) => {
    setCurrentDescription(value);
    setHasChanges(true);
    
    if (selectedDescriptionChannel === 'default') {
      onDefaultDescriptionChange(value);
    } else {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        saveChannelDescription(selectedDescriptionChannel, value);
      }, 3000);
    }
  };

  const handleChannelSwitch = (newChannelId) => {
    if (hasChanges && selectedDescriptionChannel !== 'default') {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveChannelDescription(selectedDescriptionChannel, currentDescription);
    }
    
    setSelectedDescriptionChannel(newChannelId);
  };

  const handleResetToDefault = (e) => {
    e.preventDefault();
    saveChannelDescription(selectedDescriptionChannel, '');
    ToastSuccess('Kanaal teruggezet naar standaard omschrijving', 'kanaal_reset', 2000);
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="mb-2">
      <div className="d-flex flex-row justify-content-between align-items-center mb-2">
        <label className="inputTitle fw-medium">{label}</label>
        {salesChannels.length > 0 && (
          <div className="d-flex align-items-baseline gap-2">
            <label className="inputTitle fw-medium" style={{ fontSize: '14px', marginTop: '2px', marginBottom: '0px' }}>
              Verkoopkanaal:
            </label>
            <select
              className="standardInput standardDropdownArrow"
              style={{ 
                width: 'auto', 
                minWidth: '200px', 
                fontSize: '14px', 
                margin: 0, 
                paddingTop: '6px', 
                paddingBottom: '6px', 
                height: '36px' 
              }}
              value={selectedDescriptionChannel}
              onChange={(e) => handleChannelSwitch(e.target.value)}
            >
              <option value="default">Standaard</option>
              {salesChannels.map((channel) => (
                <option key={channel.sales_channel_id} value={channel.sales_channel_id}>
                  {channel.sales_channel_name}
                  {channelDescriptions[channel.sales_channel_id] ? ' ✓' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {selectedDescriptionChannel !== 'default' && (
        <small className="text-muted d-block mb-2" style={{ fontSize: '12px' }}>
          {channelDescriptions[selectedDescriptionChannel] 
            ? 'Deze omschrijving wordt gebruikt voor dit verkoopkanaal' 
            : 'Nog geen specifieke omschrijving - standaard omschrijving wordt gebruikt'}
          {hasChanges && (
            <span className="ms-2 text-warning">
              • Wijzigingen worden automatisch opgeslagen...
            </span>
          )}
          {channelDescriptions[selectedDescriptionChannel] && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-3"
              onClick={handleResetToDefault}
              style={{ fontSize: '10px', padding: '2px 6px' }}
            >
              Reset naar standaard
            </button>
          )}
        </small>
      )}
      
      {useTextarea ? (
        <textarea
          className="borderInputs rounded-2 w-100"
          value={currentDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onBlur={(e) => {
            if (hasChanges) {
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
              }
              if (selectedDescriptionChannel === 'default') {
                onDefaultDescriptionChange(e.target.value);
              } else {
                saveChannelDescription(selectedDescriptionChannel, e.target.value);
              }
            }
          }}
          rows={4}
        />
      ) : (
        <TextEditor
          key={`editor-${selectedDescriptionChannel}-${editorKey.current}`}
          onBlur={(value) => {
            handleDescriptionChange(value);
          }}
          value={currentDescription}
        />
      )}
    </div>
  );
};

export default SalesChannelDescriptionEditor;
